// Reception landing (Phase 1a of the revised EMR framework, 2026-10-03).
//
// Reception staff land here. Six card types are visible as the primary
// choice; each card eventually opens its own registration flow or search.
// For Phase 1a this screen is a shell — the forms, Find-before-create
// search, and the family + HMO pickers all arrive in Phase 1b / 1c.
//
// What this screen deliberately does NOT do yet:
//   * No patient create / update. The existing phone-based patient lookup
//     continues to work for the clinical team via /encounters/new.
//   * No encounter linkage. Opening a card from here does not start a
//     visit (that integration is Phase 1b, when encounters.patient_card_id
//     is added).
//   * No search bar wired to the server. The input is UI-only here so the
//     layout survives the Phase 1b API work without a visual jolt.
//
// What it DOES do:
//   * Lands the receptionist role on a card-first surface, matching the
//     clinic feedback's expected mental model.
//   * Signals clearly which card types are coming, so staff walkthroughs
//     during Phase 1a can validate labels and layout before forms land.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type CardKey =
    | 'personal'
    | 'family'
    | 'corporate'
    | 'gynae'
    | 'anc'
    | 'child-health'
    | 'emergency';

interface CardDef {
    key: CardKey;
    title: string;
    tagline: string;
    description: string;
}

// Order mirrors the clinic feedback doc: Personal first, then Family,
// Corporate, Gynae, ANC/Maternity, Emergency.
const CARDS: ReadonlyArray<CardDef> = [
    {
        key: 'personal',
        title: 'Personal card',
        tagline: 'Individual patient',
        description:
            'Register or find one patient. Opens the full personal form (sections A–H) and visit history.',
    },
    {
        key: 'family',
        title: 'Family card',
        tagline: 'Shared account, separate records',
        description:
            'Create or find a family account. Add beneficiaries with their own medical history. HMO per beneficiary is supported.',
    },
    {
        key: 'corporate',
        title: 'Corporate card',
        tagline: 'Employer / corporate payer',
        description:
            'Link a patient to an employer. Captures staff reference and billing arrangement without granting the employer clinical access.',
    },
    {
        key: 'gynae',
        title: 'Gynae card',
        tagline: 'Gynaecology designation',
        description:
            'Mark an existing patient file with the gynae designation. Earlier history is retained; specialty fields follow clinic validation.',
    },
    {
        key: 'anc',
        title: 'ANC / Maternity card',
        tagline: 'Antenatal programme',
        description:
            'Open the patient file under the antenatal / maternity programme. Visit templates land after clinic walkthrough.',
    },
    {key: 'child-health', title: 'Child health / Paediatrics card', tagline: 'Birth to ongoing child care', description: 'Link a child to a maternity delivery or register a child born elsewhere. Keep a separate patient record with check-ins, growth and immunisation history.'},
    {
        key: 'emergency',
        title: 'Emergency card',
        tagline: 'Urgent intake',
        description:
            'Create an urgent visit with whatever identifiers are available. Missing information can be completed later.',
    },
];

export function ReceptionScreen() {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');

    // Phase 1c: all 6 cards live. Personal/Gynae/ANC/Emergency share the
    // generic CardTypeRoute (same shape). Family and Corporate have their
    // own quirks (family account + beneficiaries; Corporate adds employer
    // fields) handled inside those screens.
    function onCardClick(key: CardKey) {
        if (key === 'child-health') { navigate('/child-health'); return; }
        if (key === 'personal') { navigate('/reception/personal'); return; }
        navigate(`/reception/card/${key}`);
    }

    // Client-side filter stub so the layout behaves correctly once the
    // Phase 1b search endpoint lands. For now it only filters the visible
    // card copy against the query, not real patient data.
    const filteredCards = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return CARDS;
        return CARDS.filter(
            (c) =>
                c.title.toLowerCase().includes(q)
                || c.tagline.toLowerCase().includes(q)
                || c.description.toLowerCase().includes(q),
        );
    }, [search]);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-slate-900 text-2xl font-semibold">Reception</h1>
                <p className="text-slate-500 text-sm mt-1">
                    Start a new registration or find an existing patient. Pick the card type the clinic uses for this visit.
                </p>
            </div>

            <div className="pm-card p-3">
                <label htmlFor="reception-search" className="sr-only">
                    Find existing patient or family
                </label>
                <input
                    id="reception-search"
                    type="search"
                    className="pm-input"
                    placeholder="Find by hospital number, name, phone, or date of birth"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <p className="text-slate-400 text-xs mt-2">
                    Search is a preview — connects to patient and family records in Phase 1b.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredCards.map((card) => {
                    return (
                        <button
                            type="button"
                            key={card.key}
                            onClick={() => onCardClick(card.key)}
                            className="pm-card p-4 text-left transition border border-slate-200 hover:border-slate-300"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <div className="text-slate-900 font-semibold">{card.title}</div>
                                    <div className="text-slate-500 text-xs mt-0.5">{card.tagline}</div>
                                </div>
                                <span className="text-slate-400 text-xs uppercase tracking-wide">
                                    {card.key}
                                </span>
                            </div>
                            <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                                {card.description}
                            </p>
                        </button>
                    );
                })}
            </div>

        </div>
    );
}
